import { useState, type ReactNode } from "react";
import type { CaseOpening, ProloguePage } from "@pushback-and-chaos/client";
import { PLAY } from "../design/copy.js";
import { Pill } from "../ui/primitives.js";
import { PROLOGUE_HEROES, PROLOGUE_PAGE_HEROES } from "../generated/prologue-heroes.generated.js";
import { optimizeSvgDelivery } from "../environment/responsive-image.js";

/**
 * Frames 9 and 10 — the PROLOGUE PAGES: authored, human-written story pages
 * that run before the game interface boots (story-legibility phase, step 1).
 * The copy is served verbatim from the case bible through packages/client
 * and rendered character-for-character — never generated, paraphrased,
 * summarised, or LLM-touched. `beat.golden.test.tsx` asserts the rendered
 * DOM contains every authored block in full.
 *
 * Layout per the frames: left column type over the persistent nav (eyebrow
 * in Museo 700, case name in 300, prose at the column's foot), hero image
 * right, a single `next ▷` affordance bottom-left of the text column. The
 * final `next` transitions to the game interface.
 *
 * Frame 9-10's plate is a stock photograph of a building that is not a
 * veterinary clinic (docs/DESIGN-SYSTEM.md ambiguity A6). No stock image is
 * shipped; the slot renders a generated plate in the frame's exact
 * proportions until real case art exists.
 */
export function BeatPresentation({
  opening,
  index,
  onNext,
  onEnterPlay,
  resuming = false,
}: {
  opening: CaseOpening;
  index: number;
  onNext: () => void;
  onEnterPlay: () => void;
  /** True when the run behind this prologue is already under way: the last page says so. */
  resuming?: boolean;
}): ReactNode {
  const pages = opening.prologuePages;
  const page = pages[Math.min(index, pages.length - 1)] ?? pages[0];
  const isLast = index >= pages.length - 1;

  if (page === undefined) return null;

  return (
    <div className="flex min-h-[calc(100svh-6rem)] gap-12 px-gutter pb-10">
      <section className="flex w-[31%] flex-col">
        <h1 className="m-0 font-display text-title">
          <span className="font-display font-bold">{page.eyebrow}</span>
          <br />
          {page.title.split(" ").slice(0, 1).join(" ")}
          <br />
          {page.title.split(" ").slice(1).join(" ")}
        </h1>

        <div className="mt-auto max-w-[30rem] font-sans text-body">
          <PrologueBlocks blocks={page.blocks} pageKey={String(page.index)} />
        </div>

        <div className="mt-10 flex justify-end pr-8">
          <Pill onClick={isLast ? onEnterPlay : onNext}>
            <span>{isLast && resuming ? PLAY.walkBackIn : PLAY.next}</span>
            <span aria-hidden="true">{PLAY.nextCaret}</span>
          </Pill>
        </div>
      </section>

      <section aria-hidden="true" className="relative flex-1 overflow-hidden">
        <HeroPlate slot={page.heroImageSlot} caseSlug={opening.slug} pageIndex={page.index} />
      </section>
    </div>
  );
}

/**
 * THE AUTHORED BLOCKS OF ONE PROLOGUE PAGE, rendered character-for-character.
 * Shared by the standalone prologue (the library's "read the prologue.") and
 * by the play surface, where the prologue is the book's first chapters
 * (2026-09-22): one renderer, so the golden test's guarantee — every block,
 * verbatim — holds wherever the pages are read.
 */
export function PrologueBlocks({
  blocks,
  pageKey,
}: {
  blocks: readonly { readonly kind: "paragraph" | "dialogue" | "learner_dialogue"; readonly text: string }[];
  pageKey: string;
}): ReactNode {
  return (
    <>
      {blocks.map((block, i) =>
        block.kind === "learner_dialogue" ? (
          // The player character's own line: the same treatment as the
          // learner's words in the play thread (right-aligned, soft ink,
          // the rule on the right), so the prologue already reads as a
          // conversation the learner is part of.
          <p
            key={`${pageKey}-${String(i)}`}
            className="pc-prologue-learner mt-0 mb-5 ml-auto max-w-[45ch] border-r-2 border-r-grid-line-major pr-4 text-right font-sans text-small text-ink-soft whitespace-pre-line last:mb-0"
          >
            {block.text}
          </p>
        ) : (
          <p key={`${pageKey}-${String(i)}`} className="pc-prologue-text mt-0 mb-5 whitespace-pre-line last:mb-0">
            {block.text}
          </p>
        ),
      )}
    </>
  );
}

/**
 * The hero image slot (phase brief section 8): the AUTHORED prologue heroes,
 * rendered at build time by the same section renderer (ADR 0009) and
 * inlined — zero data dependency, zero requests, instant paint. Slot names
 * map positionally: prologue_hero_1 -> the first authored hero, and so on.
 * Falls back to the old generated plate only if a slot has no hero.
 */
export function HeroPlate({ slot, caseSlug, pageIndex }: { slot: string; caseSlug?: string; pageIndex?: number }): ReactNode {
  const [failedScene, setFailedScene] = useState<string | null>(null);
  const index = Number.parseInt(slot.replace(/\D/g, ""), 10);
  // The hero staged for this case and page, if there is one; otherwise the
  // page's own slot, which is how every page was illustrated before 2026-09-18.
  const staged = caseSlug === undefined || pageIndex === undefined ? undefined : PROLOGUE_PAGE_HEROES[`${caseSlug}/${String(pageIndex)}`];
  const hero = (staged === undefined ? undefined : PROLOGUE_HEROES.find((candidate) => candidate.sceneId === staged)) ?? PROLOGUE_HEROES[Number.isFinite(index) ? index - 1 : -1];
  if (hero !== undefined) {
    return (
      <div
        className="pc-hero-plate h-full w-full"
        role="img"
        aria-label={hero.description}
        style={{ color: "var(--pc-section-ink)", background: "var(--pc-section-paper)", ["--pc-grid-bg" as string]: "var(--pc-section-paper)" }}
        onErrorCapture={(event) => {
          if (!(event.target instanceof Element)) return;
          const original = event.target.getAttribute("data-png-fallback");
          if (original !== null) { event.target.removeAttribute("data-png-fallback"); event.target.setAttribute("href", original); }
          else if (event.target.tagName.toLowerCase() === "image") setFailedScene(hero.sceneId);
        }}
        dangerouslySetInnerHTML={{ __html: failedScene === hero.sceneId ? hero.recoverySvg : optimizeSvgDelivery(hero.svg, 1536) }}
      />
    );
  }
  return (
    <div
      className="h-full w-full"
      style={{
        background:
          "radial-gradient(120% 90% at 78% 12%, #1a3a72 0%, #0d1f3d 42%, #06101f 100%), linear-gradient(200deg, rgba(224,178,58,0.55) 0%, rgba(0,0,0,0) 46%)",
      }}
    />
  );
}

export type { ProloguePage };

export function useBeatPager(): { index: number; next: () => void; reset: () => void } {
  const [index, setIndex] = useState(0);
  return { index, next: () => setIndex((i) => i + 1), reset: () => setIndex(0) };
}
